export const ELLIPSIS_MARKER = 'ellipsis';

const getRange = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

interface GetPaginationItemsOptions {
  currentPage: number;
  numPages: number;
  maxContiguousPages?: number;
}

export const getPaginationItems = ({
  currentPage,
  numPages,
}: GetPaginationItemsOptions) => {
  if (numPages <= 6) {
    return getRange(1, numPages);
  }

  let startRange: number[];
  let endRange: number[];
  let siblingRange: number[] | undefined;
  let startEllipsis: (typeof ELLIPSIS_MARKER)[];
  let endEllipsis: (typeof ELLIPSIS_MARKER)[];

  // START RANGE
  // Current page near start => start range shows 1 through 4 without ellipsis
  if (currentPage <= 3) {
    startRange = getRange(1, 4);
    siblingRange = [];
    startEllipsis = [];
    // Else => start range shows just 1 with ellipsis
  } else {
    startRange = [1];
    startEllipsis = [ELLIPSIS_MARKER];
  }

  // END RANGE
  // Current page near end => end range shows last 4 pages without ellipsis
  if (currentPage >= numPages - 2) {
    endRange = getRange(numPages - 3, numPages);
    siblingRange = [];
    endEllipsis = [];
    // Else => end range shows just last page with ellipsis before it
  } else {
    endRange = [numPages];
    endEllipsis = [ELLIPSIS_MARKER];
  }

  if (!siblingRange) {
    siblingRange = [currentPage, currentPage + 1];
  }

  return [
    ...startRange,
    ...startEllipsis,
    ...siblingRange,
    ...endEllipsis,
    ...endRange,
  ];
};

interface GetNumberOfPagesOptions {
  itemsPerPage?: number;
  numItems?: number;
}

export const getNumberOfPages = ({
  numItems,
  itemsPerPage,
}: GetNumberOfPagesOptions) => {
  if (numItems && itemsPerPage) {
    return Math.ceil(numItems / itemsPerPage);
  }

  return 1;
};

interface GetItemRangeForPageOptions {
  currentPage: number;
  itemsPerPage: number;
  numItems: number;
}

export const getItemRangeForPage = ({
  itemsPerPage,
  currentPage,
  numItems,
}: GetItemRangeForPageOptions) => {
  const maxPage = Math.ceil(numItems / itemsPerPage);
  const minPage = 1;
  const safeCurrentPage = Math.max(minPage, Math.min(currentPage, maxPage));

  const rangeStart = (safeCurrentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(safeCurrentPage * itemsPerPage, numItems);

  return [rangeStart, rangeEnd] as const;
};
